import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useAuth } from "context/authContext";
import logo from "assets/logos/logo.png";
import teamworkIllustration from "assets/images/teamwork.svg";
import { useAppTheme } from "context/themeContext";

function Basic() {
  const [id, setID] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { primaryColor } = useAppTheme();
  const handleSignin = async () => {
    setLoading(true);
    const output = await login(id, password);
    setError("");
    if (output === "Done") {
      navigate("/dashboard");
    } else {
      setError(output);
    }
    setLoading(false);
  };

  return (
    <Grid container sx={{ minHeight: "100vh" }}>
      {/* Left Branding Panel */}
      <Grid
        item
        xs={12}
        md={5}
        sx={{
          background: isSmallScreen
            ? "linear-gradient(135deg, #f0f4f8 0%, #ffffff 100%)"
            : "linear-gradient(135deg, #e0f7fa 0%, #f0f4f8 100%)", // More visually appealing
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: 4,
          py: isSmallScreen ? 4 : 0,
          textAlign: "center",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="HR Health One Logo"
          sx={{
            width: isSmallScreen ? 100 : 140,
            mb: 2,
          }}
        />
        <Typography
          variant={isSmallScreen ? "h4" : "h3"}
          fontWeight="bold"
          color={primaryColor}
          gutterBottom
        >
          HJ Management Services
        </Typography>
        {!isSmallScreen && (
          <>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 400, mb: 4 }}>
              Empowering organizations through strategic insight, customized solutions, and
              cross-industry expertise.
            </Typography>
            <Box
              component="img"
              src={teamworkIllustration}
              alt="Teamwork"
              sx={{
                width: "100%",
                maxWidth: 280,
                height: "auto",
                mt: 2,
              }}
            />
          </>
        )}
      </Grid>

      {/* Right Panel – Login */}
      <Grid
        item
        xs={12}
        md={7}
        sx={{
          background: "linear-gradient(135deg, #f0f4f8 0%, #ffffff 100%)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          py: 6,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 400,
            p: 4,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <Box textAlign="center" mb={3}>
            <Typography variant="h5" fontWeight="bold" color={primaryColor}>
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please enter your credentials to continue
            </Typography>
          </Box>

          <form>
            <MDInput
              type="text"
              label="Employee ID"
              value={id}
              onChange={(e) => setID(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <MDInput
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />

            {loading ? (
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <MDButton
                fullWidth
                variant="gradient"
                color="primary"
                onClick={handleSignin}
                sx={{
                  backgroundColor: primaryColor,
                  "&:hover": { backgroundColor: "#005e6b" },
                  mt: 1,
                }}
              >
                Sign In
              </MDButton>
            )}
          </form>

          {error && (
            <Typography mt={2} color="error" variant="body2" textAlign="center">
              {error}
            </Typography>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

export default Basic;
